/**
 * API error taxonomy.
 *
 * Designed for institutional defaults:
 *   • `ApiError` carries the HTTP status, the parsed body, and the original
 *     request id so audit-log linking is one line at the call site.
 *   • Specialised subclasses (`ApiAuthError`, `ApiValidationError`, …) let
 *     React Query's retry/onError predicates be precise — never retry 4xx,
 *     always sign-out on 401, surface 409 as conflict UI etc.
 *   • The `detail` is always a string when present (we collapse 422 arrays
 *     into a friendly summary via `formatValidationDetail`).
 */

import type { ApiValidationErrorBody } from '@/types/api';

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'auth'              // 401
  | 'forbidden'         // 403
  | 'not_found'         // 404
  | 'conflict'          // 409
  | 'validation'        // 422
  | 'rate_limited'      // 429
  | 'server'            // 5xx
  | 'unknown';

export interface ApiErrorMeta {
  url: string;
  method: string;
  status: number;
  request_id: string;
  /** Number of network attempts made before giving up. */
  attempts: number;
  /** Mock side-channel hit. */
  mock: boolean;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly meta: ApiErrorMeta;
  readonly body: unknown;

  constructor(message: string, kind: ApiErrorKind, meta: ApiErrorMeta, body: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.meta = meta;
    this.body = body;
  }
}

/**
 * Surface a backend HTTPException string `detail` as the error message when
 * present, else a generic fallback. FastAPI puts business-rule messages in
 * `body.detail` (e.g. the start-before-arm 409: "Deployment provisioned but
 * not armed — arm its subscription, then start again."). Without this the UI
 * showed a useless "Conflict (409)." instead of the actionable reason.
 */
const detailOr = (body: unknown, fallback: string): string => {
  const d = (body as { detail?: unknown } | null)?.detail;
  return typeof d === 'string' && d.trim() !== '' ? d : fallback;
};

export class ApiAuthError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super('Authentication failed (401).', 'auth', meta, body);
    this.name = 'ApiAuthError';
  }
}

export class ApiForbiddenError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super(detailOr(body, 'Forbidden (403).'), 'forbidden', meta, body);
    this.name = 'ApiForbiddenError';
  }
}

export class ApiNotFoundError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super(detailOr(body, 'Not found (404).'), 'not_found', meta, body);
    this.name = 'ApiNotFoundError';
  }
}

export class ApiConflictError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super(detailOr(body, 'Conflict (409).'), 'conflict', meta, body);
    this.name = 'ApiConflictError';
  }
}

export class ApiValidationError extends ApiError {
  readonly issues: ApiValidationErrorBody['detail'];
  constructor(meta: ApiErrorMeta, body: ApiValidationErrorBody) {
    super(formatValidationDetail(body), 'validation', meta, body);
    this.name = 'ApiValidationError';
    this.issues = body.detail;
  }
}

export class ApiRateLimitError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super('Rate limited (429).', 'rate_limited', meta, body);
    this.name = 'ApiRateLimitError';
  }
}

export class ApiServerError extends ApiError {
  constructor(meta: ApiErrorMeta, body: unknown = null) {
    super(detailOr(body, `Server error (${meta.status}).`), 'server', meta, body);
    this.name = 'ApiServerError';
  }
}

export class ApiNetworkError extends ApiError {
  constructor(meta: ApiErrorMeta, cause: unknown = null) {
    super('Network failure — could not reach the API.', 'network', meta, cause);
    this.name = 'ApiNetworkError';
  }
}

export class ApiTimeoutError extends ApiError {
  constructor(meta: ApiErrorMeta) {
    super('Request timed out.', 'timeout', meta);
    this.name = 'ApiTimeoutError';
  }
}

export class ApiAbortedError extends ApiError {
  constructor(meta: ApiErrorMeta) {
    super('Request aborted.', 'aborted', meta);
    this.name = 'ApiAbortedError';
  }
}

/* ============================================================================
 * Body parsing helpers
 * ============================================================================ */

export const formatValidationDetail = (body: ApiValidationErrorBody): string => {
  // FastAPI HTTPException(detail="...") yields a STRING detail (our custom
  // business-rule 422s, e.g. the asset-class deployment gate). Surface it
  // verbatim instead of collapsing to a generic message.
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim() !== '') {
    return detail;
  }
  if (!Array.isArray(body.detail) || body.detail.length === 0) {
    return 'Validation failed.';
  }
  const first = body.detail[0];
  if (first === undefined) {
    return 'Validation failed.';
  }
  const path = first.loc.filter((p) => p !== 'body').join('.');
  return `Validation: ${path !== '' ? `${path} — ` : ''}${first.msg}`;
};

/** Is this exception retryable on a network attempt? */
export const isRetryableError = (err: unknown): boolean => {
  if (err instanceof ApiNetworkError || err instanceof ApiServerError || err instanceof ApiTimeoutError) {
    return true;
  }
  return false;
};

/** Is the error definitely auth-bad — sign the user out. */
export const isAuthError = (err: unknown): err is ApiAuthError => err instanceof ApiAuthError;
